import { getId, MessageBarType } from '@fluentui/react';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useOptionalNotifications } from '@shared/lib/ui/components-alpha/useNotifications';
import { useOptionalToast } from '@shared/lib/ui/components-alpha/useToast';

import { IMessageBarMessage, IStyleFunctionOrObject } from '../models/components.models';
import { ThemeContext } from './contexts';
import { getNotificationGroupId, toLegacyNotification, toV2NotificationInput } from './notificationAdapter';
import { queryDesktop, queryMobile, queryNoDesktop } from './styles';
import { getToastGroupId, type LegacyAddToastInput, toLegacyMessage, toV2Input } from './toastAdapter';
import { getClassNames, getMessageBarMessage, getStyles } from './utils';

export const useTheme = () => useContext(ThemeContext);

export const useClassNames = <S, C>(
  prefix: string,
  defaultStyleFunctionOrObject?: IStyleFunctionOrObject<S>,
  styleFunctionOrObject?: IStyleFunctionOrObject<S> | IStyleFunctionOrObject<S>[],
  ...classNames: (string | undefined | boolean)[]
) => {
  const theme = useTheme();
  return getClassNames(prefix, theme, defaultStyleFunctionOrObject, styleFunctionOrObject, ...classNames) as C;
};

export const useThemeStyles = <S>(
  defaultStyleFunctionOrObject?: IStyleFunctionOrObject<S>,
  styleFunctionOrObject?: IStyleFunctionOrObject<S> | IStyleFunctionOrObject<S>[]
) => {
  const theme = useTheme();
  return getStyles(theme, defaultStyleFunctionOrObject, styleFunctionOrObject);
};

// Message Bar list can be display on a page instead of a toast
export function useMessageBarList(msgs: IMessageBarMessage[] = []) {
  const [messages, setMessages] = useState<IMessageBarMessage[]>(msgs);
  const addMessage = useCallback(
    (message: string, timeout = 0, groupId = '', messageBarType: MessageBarType = MessageBarType.error) =>
      setMessages([...messages, getMessageBarMessage(message, timeout, groupId, messageBarType)]),
    [messages]
  );
  const removeMessage = useCallback(
    (message: ReactNode) => setMessages(messages.slice().filter((t) => t.message !== message)),
    [messages]
  );
  const removeGroupMessages = useCallback(
    (groupId: string) => setMessages(messages.slice().filter((t) => t.groupId !== groupId)),
    [messages]
  );
  const removeAllMessages = useCallback(() => setMessages([]), []);
  return { messages, addMessage, removeMessage, removeGroupMessages, removeAllMessages } as const;
}

// No-op fallback so the legacy adapter never throws when rendered outside a `ToastProvider`
// (the old legacy ToastContext had a no-op default; isolated unit tests/Storybook rely on it).
type ToastV2 = NonNullable<ReturnType<typeof useOptionalToast>>;
const NOOP_TOAST: ToastV2 = {
  toasts: [],
  addToast: (input) => input.id ?? getId(),
  removeToast: () => undefined,
  removeAllToasts: () => undefined,
};

// Thin compatibility adapter over the single (new) toast context. It preserves the legacy
// `IMessageBarMessage` API so the ~37 legacy call sites and the legacy `Toast` UI keep working,
// while all state (incl. deduplication) lives in `@shared/lib/ui/components-alpha`.
export function useToast() {
  const v2 = useOptionalToast() ?? NOOP_TOAST;
  const messages = useMemo(() => v2.toasts.map(toLegacyMessage), [v2.toasts]);
  const addToast = useCallback(
    (message: LegacyAddToastInput) => {
      const id = v2.addToast(toV2Input(message));
      return { ...message, id };
    },
    [v2.addToast]
  );
  const removeToast = useCallback(
    (message: ReactNode) => {
      v2.toasts.filter((t) => t.message === message).forEach((t) => v2.removeToast(t.id));
    },
    [v2.removeToast, v2.toasts]
  );
  const removeGroupToasts = useCallback(
    (groupId: string) => {
      v2.toasts.filter((t) => getToastGroupId(t) === groupId).forEach((t) => v2.removeToast(t.id));
    },
    [v2.removeToast, v2.toasts]
  );
  const removeAllToasts = v2.removeAllToasts;
  return { messages, addToast, removeToast, removeGroupToasts, removeAllToasts } as const;
}

// No-op fallback so the legacy adapter never throws when rendered outside a `NotificationsProvider`
// (the old legacy NotificationContext had a no-op default; isolated unit tests/Storybook rely on it).
type NotificationsV2 = NonNullable<ReturnType<typeof useOptionalNotifications>>;
const NOOP_NOTIFICATIONS: NotificationsV2 = {
  notifications: [],
  addNotification: (input) => input.id ?? getId(),
  readNotification: () => undefined,
  removeNotification: () => undefined,
  removeAllNotifications: () => undefined,
};

// Thin compatibility adapter over the single (new) notification context. It preserves the legacy
// `IMessageBarMessage` API so the legacy call sites and the legacy bell `Notification` UI keep
// working, while all state lives in `@shared/lib/ui/components-alpha`.
export function useNotification() {
  const v2 = useOptionalNotifications() ?? NOOP_NOTIFICATIONS;
  const messages = useMemo(() => v2.notifications.map(toLegacyNotification), [v2.notifications]);
  const addNotification = useCallback(
    (message: IMessageBarMessage) => {
      const id = v2.addNotification(toV2NotificationInput(message));
      return { ...message, id };
    },
    [v2.addNotification]
  );
  const addNotifications = useCallback(
    (messages: IMessageBarMessage[]) =>
      messages.map((msg) => ({ ...msg, id: v2.addNotification(toV2NotificationInput(msg)) })),
    [v2.addNotification]
  );
  const readNotification = useCallback(
    (payload: string | undefined) => {
      v2.readNotification(payload);
      return payload;
    },
    [v2.readNotification]
  );
  const removeNotification = useCallback(
    (payload: string | undefined) => {
      v2.removeNotification(payload);
      return payload;
    },
    [v2.removeNotification]
  );
  const removeGroupNotifications = useCallback(
    (groupId: string) => {
      v2.notifications.filter((n) => getNotificationGroupId(n) === groupId).forEach((n) => v2.removeNotification(n.id));
    },
    [v2.notifications, v2.removeNotification]
  );
  const removeAllNotifications = v2.removeAllNotifications;
  return {
    messages,
    addNotification,
    addNotifications,
    readNotification,
    removeNotification,
    removeGroupNotifications,
    removeAllNotifications,
  } as const;
}

export function usePageTitle(pageTitle: string) {
  useEffect(() => {
    const title = `${pageTitle}`;
    if (pageTitle && title !== document.title) {
      document.title = title;
    }
  }, [pageTitle]);
}

export const useDimensions = (
  ref: React.RefObject<HTMLElement>,
  isClientWidth?: boolean,
  onResize?: (dimensions: { width: number; height: number }) => void
) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (el) {
      const getDimensions = () => ({
        width: el ? (isClientWidth ? el.clientWidth : el.offsetWidth) : 0,
        height: el ? (isClientWidth ? el.clientHeight : el.offsetHeight) : 0,
      });
      const handleResize = () => {
        const d = getDimensions();
        setDimensions(d);
        onResize && onResize(d);
      };
      const d = getDimensions();
      setDimensions(d);
      onResize && onResize(d);
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(el);
      return () => {
        resizeObserver.unobserve(el);
      };
    }
    // for lint warning
    return;
  }, [ref, isClientWidth, onResize]);
  return dimensions;
};

export function useMediaQuery() {
  const [isDesktop, setIsDesktop] = useState<boolean>(window.matchMedia(queryDesktop).matches);
  const [isNoDesktop, setIsNoDesktop] = useState<boolean>(window.matchMedia(queryNoDesktop).matches);
  const [isMobile, setIsMobile] = useState<boolean>(window.matchMedia(queryMobile).matches);

  useEffect(() => {
    const desktopQuery = window.matchMedia(queryDesktop);
    const noDesktopQuery = window.matchMedia(queryNoDesktop);
    const mobileQuery = window.matchMedia(queryMobile);

    const handleChange = () => {
      setIsDesktop(desktopQuery.matches);
      setIsNoDesktop(noDesktopQuery.matches);
      setIsMobile(mobileQuery.matches);
    };

    desktopQuery.addEventListener('change', handleChange);
    noDesktopQuery.addEventListener('change', handleChange);
    mobileQuery.addEventListener('change', handleChange);

    handleChange();

    return () => {
      desktopQuery.removeEventListener('change', handleChange);
      noDesktopQuery.removeEventListener('change', handleChange);
      mobileQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return { isDesktop, isNoDesktop, isMobile };
}
